/**
 * Store de estado para nodos de red
 * @module stores/useNodeStore
 */

import { create } from 'zustand'
import { AnyNode, NodeType } from '@/types/nodes'
import { nodeService } from '@/services/nodeService'
import { nodeServiceApi } from '@/services/nodeServiceApi'

// ============================================================================
// TYPES
// ============================================================================

interface NodeStore {
  // Estado
  nodes: AnyNode[]
  selectedNodeId: string | null
  loading: boolean
  error: string | null

  // Acciones
  loadNodes: () => Promise<void>
  addNode: (node: AnyNode) => Promise<AnyNode>
  updateNode: (node: AnyNode) => Promise<void>
  deleteNode: (id: string) => Promise<void>
  selectNode: (id: string | null) => void
  getNodeById: (id: string) => AnyNode | undefined
  getNodesByType: (type: NodeType) => AnyNode[]
  searchNodes: (query: string) => AnyNode[]
  clearError: () => void
}

// ============================================================================
// STORE
// ============================================================================

export const useNodeStore = create<NodeStore>((set, get) => ({
  // Estado inicial
  nodes: [],
  selectedNodeId: null,
  loading: false,
  error: null,

  // Acciones
  loadNodes: async () => {
    set({ loading: true, error: null })
    try {
      const nodes = await nodeServiceApi.getAll()
      set({ nodes, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error cargando nodos',
        loading: false,
      })
    }
  },

  addNode: async (node: AnyNode) => {
    set({ loading: true, error: null })
    try {
      const createdNode = await nodeServiceApi.create(node)
      set((state) => ({
        nodes: [...state.nodes, createdNode],
        loading: false,
      }))
      return createdNode
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error agregando nodo',
        loading: false,
      })
      throw error
    }
  },

  updateNode: async (node: AnyNode) => {
    set({ loading: true, error: null })
    try {
      const updatedNode = await nodeServiceApi.update(node.id, node)
      set((state) => ({
        nodes: state.nodes.map((n) => (n.id === node.id ? updatedNode : n)),
        loading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error actualizando nodo',
        loading: false,
      })
    }
  },

  deleteNode: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await nodeServiceApi.delete(id)
      set((state) => ({
        nodes: state.nodes.filter((n) => n.id !== id),
        selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
        loading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error eliminando nodo',
        loading: false,
      })
    }
  },

  selectNode: (id: string | null) => {
    set({ selectedNodeId: id })
  },

  getNodeById: (id: string) => {
    return get().nodes.find((n) => n.id === id)
  },

  getNodesByType: (type: NodeType) => {
    return get().nodes.filter((n) => n.tipo === type)
  },

  searchNodes: (query: string) => {
    return nodeService.searchNodes(get().nodes, query)
  },

  clearError: () => {
    set({ error: null })
  },
}))
